import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS, type PaymentDto, type AnalyticsPaymentProvider } from '@neke/core';
import type { ApiError } from '../api/client';
import { api } from '../api/client';
import { useSession } from '../store/session';
import { track } from '../analytics';

/** Провайдер в ответе сервера назван как в базе, в аналитике — как в словаре. */
const PROVIDER: Record<PaymentDto['provider'], AnalyticsPaymentProvider> = {
  KASPI: 'kaspi',
  APPLE: 'app_store',
  GOOGLE: 'play',
};

/**
 * Валюта суммы. Знаем её только для кассы: Kaspi считает в тенге, и в ответе
 * сервера валюты поэтому нет. У покупки в сторе сумма приходит в валюте
 * аккаунта — приложение шлёт её само, а этому экрану взять её неоткуда.
 *
 * Для чужого канала лучше промолчать, чем подставить тенге: совравшая валюта
 * складывает доллары с тенге в одном столбце и обнаруживается через месяц на
 * уже собранных данных. Как читать событие без валюты — см. `PaymentChannel`
 * в словаре.
 */
function currencyOf(provider: PaymentDto['provider']): string | undefined {
  return provider === 'KASPI' ? 'KZT' : undefined;
}

/**
 * Экран ожидания оплаты.
 *
 * Вебхуков у Kaspi нет, поэтому статус мы опрашиваем сами — с интервалом,
 * который прислал сам Kaspi. Ссылку открывает пользователь нажатием: открыть
 * её из кода после запроса нельзя, всплывающее окно заблокирует браузер.
 */
export function Payment() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { ready, user } = useSession();

  const [payment, setPayment] = useState<PaymentDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  // Опрос идёт по кругу — исход отмечаем один раз, а не на каждом ответе.
  const outcomeSent = useRef(false);

  const poll = useCallback(async (paymentId: string) => {
    try {
      const next = await api.paymentStatus(paymentId);
      setPayment(next);
      if (next.status === 'PENDING') {
        // pollIntervalMs пустой только у покупки в сторе: там статус меняют
        // confirm и вебхук, опрашивать нечего. На эту страницу такие платежи не
        // попадают — ветка нужна компилятору, а не браузеру.
        timer.current = window.setTimeout(() => void poll(paymentId), next.pollIntervalMs ?? 5000);
        return;
      }
      if (!outcomeSent.current) {
        outcomeSent.current = true;
        void track(ANALYTICS_EVENTS.paymentOutcomeSeen, {
          status: next.status,
          tier: next.tier,
          amount: next.amount,
          // Тот же шаг в приложении приходит из стора: канал и валюту ставим
          // явно, иначе суммы двух каналов лягут в один столбец.
          provider: PROVIDER[next.provider],
          currency: currencyOf(next.provider),
        });
      }
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, []);

  useEffect(() => {
    if (!ready || !id) return;
    if (!user) {
      window.location.href = `/login?next=/payment/${id}`;
      return;
    }
    void poll(id);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [ready, user, id, poll]);

  if (error)
    return (
      <div className="page">
        <div className="card">
          <p>{error}</p>
        </div>
      </div>
    );
  if (!payment)
    return (
      <div className="page">
        <p className="muted">{t('common.loading')}</p>
      </div>
    );

  const money = `${payment.amount.toLocaleString('ru-RU')} ₸`;
  const reportPath = `/pair/id/${payment.pairId}`;

  if (payment.status === 'PAID') {
    return (
      <div className="page">
        <div className="card pay-done">
          <h1>{t('payment.paidTitle')}</h1>
          <p className="muted">{t('payment.paidLead')}</p>
          <Link className="btn btn-primary" to={reportPath}>
            {t('payment.openReport')}
          </Link>
        </div>
      </div>
    );
  }

  if (payment.status !== 'PENDING') {
    return (
      <div className="page">
        <div className="card">
          <h1>{t('payment.failedTitle')}</h1>
          <p className="muted">
            {payment.status === 'EXPIRED' ? t('payment.expiredLead') : t('payment.failedLead')}
          </p>
          <Link className="btn btn-primary" to={reportPath}>
            {t('payment.retry')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="card pay-wait">
        <h1>{t('payment.title')}</h1>
        <p className="muted">{t('payment.lead', { amount: money })}</p>

        {payment.paymentLink && (
          <a
            className="btn btn-gold"
            href={payment.paymentLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              void track(ANALYTICS_EVENTS.paymentLinkOpened, {
                tier: payment.tier,
                amount: payment.amount,
                provider: PROVIDER[payment.provider],
                currency: currencyOf(payment.provider),
              })
            }
          >
            {t('payment.pay')}
          </a>
        )}

        <p className="fine waiting">
          <span className="dot" aria-hidden />
          {t('payment.waiting')}
        </p>
        <p className="fine">{t('payment.hint')}</p>
      </div>
    </div>
  );
}
